/**
 * UmiJS App Runtime 配置工厂
 *
 * 抽象三个项目中 app.tsx 的通用模式:
 * - getInitialState: 获取用户信息 + 认证状态
 * - layout: 头像菜单、页面切换守卫、错误边界
 * - rootContainer: 全局 Provider 包装
 *
 * 将三个项目中 app.tsx 的重复模式抽象为统一的运行时初始化工厂。
 */

import type React from 'react';
import type { BaseUser } from '../types/auth';

// ===== 类型定义 =====

/**
 * 应用用户信息 — 基于 BaseUser 扩展
 *
 * 将 `BaseUser` 所有字段设为可选，以适配用户信息未完全加载的场景。
 */
export interface AppUser extends Partial<BaseUser> {}

/**
 * InitialState 结构
 */
export interface AppInitialState<
  U extends AppUser = AppUser,
  E = Record<string, unknown>,
> {
  /** 当前用户信息 */
  currentUser?: U;
  /** 正在加载用户信息 */
  loading?: boolean;
  /** 额外的初始状态 (系统配置、组织信息等) */
  extra?: E;
  /** 重新获取用户信息 */
  fetchUserInfo: () => Promise<U | undefined>;
}

/**
 * 头像菜单项
 */
export interface AvatarMenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  danger?: boolean;
  onClick?: () => void;
}

/**
 * 应用运行时配置选项
 */
export interface AppRuntimeOptions<
  U extends AppUser = AppUser,
  E = Record<string, unknown>,
> {
  /**
   * 认证配置
   */
  auth: {
    /** 获取当前用户信息的 API */
    fetchUserInfo: () => Promise<U | undefined>;
    /** 登录页路径, 默认 '/login' */
    loginPath?: string;
    /** 不需要认证的页面路径列表 (默认含 loginPath) */
    guestPaths?: string[];
    /** 检测是否已登录 (如检查 token) */
    isAuthenticated?: () => boolean;
    /** 获取失败重试次数, 默认 0 */
    retryCount?: number;
    /** 重试延迟 (ms), 默认 1000 */
    retryDelay?: number;
  };

  /**
   * 布局配置
   */
  layout?: {
    /** 应用标题 */
    title?: string | ((initialState?: AppInitialState<U, E>) => string);
    /** 应用 Logo URL 或组件 */
    logo?: string | React.ReactNode;
    /** 头像菜单项 */
    avatarMenuItems?: AvatarMenuItem[];
    /** 显示用户名, 默认 true */
    showAvatarName?: boolean;
    /** 页脚 */
    footer?: React.ReactNode | (() => React.ReactNode);
    /** 页面切换回调 (可用于路由守卫) */
    onPageChange?: (
      initialState: AppInitialState<U, E> | undefined,
      location: { pathname: string },
      navigate: (path: string) => void,
    ) => void;
    /** 自定义 childrenRender 包装 (如水印) */
    childrenRender?: (
      children: React.ReactNode,
      initialState?: AppInitialState<U, E>,
    ) => React.ReactNode;
    /** 额外的 ProLayout 配置 */
    layoutProps?: Record<string, unknown>;
  };

  /**
   * 额外初始数据获取 (与 fetchUserInfo 并行执行)
   */
  fetchExtra?: () => Promise<E>;

  /**
   * 导航函数 (用于路由跳转)
   *
   * SDK 不假设路由模式，由消费方注入。
   * 如不提供，onPageChange 中的 navigate 回调将无操作。
   *
   * @example
   * ```ts
   * import { history } from '@umijs/max';
   * navigate: (path) => history.push(path),
   * ```
   */
  navigate?: (path: string) => void;

  /**
   * 初始化完成回调
   */
  onReady?: (state: AppInitialState<U, E>) => void;

  /**
   * 初始化失败回调
   */
  onError?: (error: Error) => void;
}

// ===== 内部工具 =====

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function normalizePath(pathname: string): string {
  const pathWithoutQuery = pathname.split('?')[0] || '/';
  return pathWithoutQuery.startsWith('/')
    ? pathWithoutQuery
    : `/${pathWithoutQuery}`;
}

function buildGuestPathList(
  loginPath: string,
  guestPaths?: string[],
): string[] {
  const normalizedLoginPath = normalizePath(loginPath);
  const sourcePaths =
    guestPaths && guestPaths.length > 0 ? guestPaths : [normalizedLoginPath];

  return [...new Set([normalizedLoginPath, ...sourcePaths.map(normalizePath)])];
}

/**
 * 判断路径是否匹配免认证路径列表（精确匹配或前缀匹配）
 */
function matchGuestPath(pathname: string, guestPaths: string[]): boolean {
  const currentPath = normalizePath(pathname);
  return guestPaths.some(
    (p) => currentPath === p || currentPath.startsWith(`${p}/`),
  );
}

// ===== 工厂函数 =====

/**
 * 创建 UmiJS App Runtime 配置
 *
 * 返回 `getInitialState` 和 `layout` 函数，直接导出供 UmiJS 使用。
 *
 * @example
 * ```ts
 * // app.tsx
 * import { createAppRuntime } from '@aspect/shared/config';
 *
 * const appRuntime = createAppRuntime({
 *   auth: {
 *     fetchUserInfo: () => getCurrentUser().then(res =>
 *       isApiSuccess(res) ? res.data : undefined
 *     ),
 *     loginPath: '/login',
 *     guestPaths: ['/login', '/register', '/agreement'],
 *   },
 *   layout: {
 *     title: 'My App',
 *     showAvatarName: true,
 *     avatarMenuItems: [
 *       { key: 'settings', label: '个人设置', onClick: () => history.push('/settings') },
 *       { key: 'logout', label: '退出登录', danger: true, onClick: handleLogout },
 *     ],
 *     onPageChange: (state, location, navigate) => {
 *       if (!state?.currentUser && location.pathname !== '/login') {
 *         navigate('/login');
 *       }
 *     },
 *   },
 * });
 *
 * export const getInitialState = appRuntime.getInitialState;
 * export const layout = appRuntime.layout;
 * ```
 */
export function createAppRuntime<
  U extends AppUser = AppUser,
  E = Record<string, unknown>,
>(options: AppRuntimeOptions<U, E>) {
  const {
    auth,
    layout: layoutCfg,
    fetchExtra,
    navigate: injectedNavigate,
    onReady,
    onError,
  } = options;

  const loginPath = normalizePath(auth.loginPath ?? '/login');
  const guestPaths = buildGuestPathList(loginPath, auth.guestPaths);

  /**
   * 获取当前路径 (支持 hash 和 history 模式)
   */
  function getCurrentPath(): string {
    if (typeof window === 'undefined') return '/';
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      return normalizePath(hash.replace(/^#/, '').split('?')[0] || '/');
    }
    return normalizePath(window.location.pathname);
  }

  /**
   * 带重试的用户信息获取
   */
  async function fetchUserWithRetry(): Promise<U | undefined> {
    const maxRetries = auth.retryCount ?? 0;
    const retryDelay = auth.retryDelay ?? 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const user = await auth.fetchUserInfo();
        if (user) return user;
      } catch {
        // 请求失败时等待后重试，最后一次失败后跳出循环
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }
    return undefined;
  }

  /**
   * 判断路径是否为免认证页面
   */
  function isGuestPath(pathname: string): boolean {
    return matchGuestPath(pathname, guestPaths);
  }

  // ----- getInitialState -----

  async function getInitialState(): Promise<AppInitialState<U, E>> {
    const fetchUserInfo = fetchUserWithRetry;
    const anonymousState = (): AppInitialState<U, E> => {
      return {
        currentUser: undefined,
        loading: false,
        extra: undefined,
        fetchUserInfo,
      };
    };

    // 免认证页面 — 跳过用户信息获取
    const currentPath = getCurrentPath();

    if (isGuestPath(currentPath)) {
      return anonymousState();
    }

    // 非免认证页面 — 如果提供了 isAuthenticated, 先检查
    if (auth.isAuthenticated && !auth.isAuthenticated()) {
      return anonymousState();
    }

    // 并行获取用户信息和额外数据
    try {
      const promises: [Promise<U | undefined>, Promise<E | undefined>] = [
        fetchUserWithRetry(),
        fetchExtra ? fetchExtra() : Promise.resolve(undefined),
      ];

      const [currentUser, extra] = await Promise.all(promises);

      const state: AppInitialState<U, E> = {
        currentUser,
        loading: false,
        extra,
        fetchUserInfo,
      };

      onReady?.(state);
      return state;
    } catch (error) {
      onError?.(normalizeError(error));
      return anonymousState();
    }
  }

  // ----- layout -----

  function layout(params: {
    initialState?: AppInitialState<U, E>;
    location?: { pathname: string };
    // UmiJS 注入
    [key: string]: unknown;
  }) {
    const { initialState } = params;
    const currentUser = initialState?.currentUser;

    // 标题
    const title =
      typeof layoutCfg?.title === 'function'
        ? layoutCfg.title(initialState)
        : layoutCfg?.title;

    // 头像菜单
    const avatarProps: Record<string, unknown> = {};
    if (currentUser) {
      avatarProps.src = currentUser.avatar;
      if (layoutCfg?.showAvatarName !== false) {
        avatarProps.title = currentUser.nickname || currentUser.username || '';
      }
    }

    // 将 avatarMenuItems 作为数据附加到返回值，由调用方决定如何渲染
    const avatarMenuItems = layoutCfg?.avatarMenuItems;

    // 页面切换
    const navigateFn = injectedNavigate ?? (() => {});
    const pageChangeHandler = layoutCfg?.onPageChange;
    const onPageChange = pageChangeHandler
      ? () => {
          const pathname = normalizePath(
            params.location?.pathname || getCurrentPath(),
          );
          pageChangeHandler(initialState, { pathname }, navigateFn);
        }
      : undefined;

    // 页脚
    const footerValue = layoutCfg?.footer;
    const footerRender = footerValue
      ? () =>
          typeof footerValue === 'function'
            ? (footerValue as () => React.ReactNode)()
            : footerValue
      : undefined;

    // children 包装 (水印等)
    const layoutChildrenRender = layoutCfg?.childrenRender;
    const childrenRender = layoutChildrenRender
      ? (children: React.ReactNode) =>
          layoutChildrenRender(children, initialState)
      : undefined;

    return {
      title,
      logo: layoutCfg?.logo,
      avatarProps,
      avatarMenuItems,
      onPageChange,
      footerRender,
      childrenRender,
      ...layoutCfg?.layoutProps,
    };
  }

  return {
    getInitialState,
    layout,
    /** 工具方法: 判断是否为免认证路径 */
    isGuestPath,
  };
}

/**
 * 创建默认的页面切换守卫
 *
 * 未登录用户在访问需要认证的页面时，自动跳转到登录页。
 *
 * @example
 * ```ts
 * const appRuntime = createAppRuntime({
 *   // ...
 *   layout: {
 *     onPageChange: createPageChangeGuard({
 *       loginPath: '/login',
 *       guestPaths: ['/login', '/register'],
 *     }),
 *   },
 * });
 * ```
 */
export function createPageChangeGuard<
  U extends AppUser = AppUser,
  E = Record<string, unknown>,
>(options?: {
  loginPath?: string;
  guestPaths?: string[];
}): (
  initialState: AppInitialState<U, E> | undefined,
  location: { pathname: string },
  navigate: (path: string) => void,
) => void {
  const loginPath = normalizePath(options?.loginPath ?? '/login');
  const guestPaths = buildGuestPathList(loginPath, options?.guestPaths);

  return (
    initialState: AppInitialState<U, E> | undefined,
    location: { pathname: string },
    navigate: (path: string) => void,
  ) => {
    if (
      !initialState?.currentUser &&
      !matchGuestPath(normalizePath(location.pathname), guestPaths)
    ) {
      navigate(loginPath);
    }
  };
}
